import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { SectionLabel } from "@/components/ui-bits";
import { CheckCircle2, AlertTriangle, XCircle, PlayCircle, RefreshCw, ShieldCheck, Award, FileJson, FileText, Printer } from "lucide-react";
import "@/components/meos-extra.css";

function CheckIcon({ status }) {
  if (status === "pass") return <CheckCircle2 size={16} color="#1D9E75" />;
  if (status === "warn") return <AlertTriangle size={16} color="#BA7517" />;
  return <XCircle size={16} color="#E24B4A" />;
}

function download(name, content, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

function summaryText(c) {
  return [
    "MARRSING EXECUTIVE OPERATING SYSTEM™",
    "PRODUCTION LAUNCH CERTIFICATION — EXECUTIVE SUMMARY",
    "".padEnd(60, "="),
    `Certificate ID:      ${c.certificate_id}`,
    `Issued:              ${new Date(c.issued_at).toLocaleString()}`,
    `Environment:         ${c.environment}`,
    `Build Version:       MEOS ${c.build_version}`,
    `Executive Intel Ver: ${c.eii_version}`,
    `Checks Passed:       ${c.checks_passed} / ${c.total_checks}`,
    `Warnings:            ${c.warnings}`,
    `Checks Failed:       ${c.checks_failed}`,
    `Certification Score: ${c.score}%`,
    `Certifying Executive:${c.certifying_executive?.name} (${c.certifying_executive?.email})`,
    `Digital Status:      ${c.digital_status}`,
    `Status:              ${c.status}`,
    "".padEnd(60, "="),
    "This is the official MarrsingClause® production launch record.",
  ].join("\n");
}

function printCertificate(c) {
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<html><head><title>${c.certificate_id}</title>
  <style>body{font-family:Georgia,serif;color:#0E0A1F;padding:48px;text-align:center}
  .seal{letter-spacing:.3em;text-transform:uppercase;color:#1F75A8;font-size:13px}
  h1{font-size:34px;margin:8px 0;color:#0E2A4A}.id{color:#555;letter-spacing:.08em}
  .grid{display:grid;grid-template-columns:1fr 1fr;gap:18px;max-width:560px;margin:34px auto;text-align:left}
  .l{font-size:10px;text-transform:uppercase;color:#888;letter-spacing:.06em}.v{font-size:17px;font-weight:600}
  .status{display:inline-block;margin-top:14px;background:#EAF3DE;color:#3B6D11;padding:8px 22px;border-radius:24px;font-size:14px;letter-spacing:.1em}
  .ft{margin-top:34px;color:#777;font-size:12px}</style></head><body>
  <div class="seal">Marrsing Executive Operating System™</div>
  <h1>Production Launch Certificate</h1>
  <div class="id">${c.certificate_id}</div>
  <div class="grid">
    <div><div class="l">Issued</div><div class="v">${new Date(c.issued_at).toLocaleString()}</div></div>
    <div><div class="l">Environment</div><div class="v">${c.environment}</div></div>
    <div><div class="l">Build Version</div><div class="v">MEOS ${c.build_version}</div></div>
    <div><div class="l">Executive Intelligence</div><div class="v">v${c.eii_version}</div></div>
    <div><div class="l">Checks Passed</div><div class="v">${c.checks_passed} / ${c.total_checks}</div></div>
    <div><div class="l">Score</div><div class="v">${c.score}%</div></div>
    <div><div class="l">Certifying Executive</div><div class="v">${c.certifying_executive?.name || ""}</div></div>
    <div><div class="l">Digital Status</div><div class="v">${c.digital_status}</div></div>
  </div>
  <div class="status">✓ ${c.status}</div>
  <div class="ft">Official MarrsingClause® production launch record · Positive Relationship Transformation</div>
  </body></html>`);
  w.document.close();
  setTimeout(() => w.print(), 300);
}

export default function CertifyPanel() {
  const [checks, setChecks] = useState(null);
  const [history, setHistory] = useState([]);
  const [certificate, setCertificate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [issueError, setIssueError] = useState("");

  const runChecks = useCallback(async () => {
    const { data } = await api.get("/certification/checks");
    setChecks(data);
  }, []);
  const loadHistory = useCallback(async () => {
    const { data } = await api.get("/certification/history");
    setHistory(data.items);
  }, []);

  useEffect(() => { runChecks(); loadHistory(); }, [runChecks, loadHistory]);

  const runJourneyTest = async () => {
    setBusy(true); await api.post("/journey/simulate"); await runChecks(); setBusy(false);
  };
  const issue = async () => {
    setBusy(true); setIssueError("");
    try {
      const { data } = await api.post("/certification/issue");
      setCertificate(data); await loadHistory();
    } catch (e) {
      const d = e.response?.data?.detail;
      setIssueError(typeof d === "object" ? (d.message || "Certification blocked.") : (d || "Certification blocked."));
    }
    setBusy(false);
  };

  if (!checks) return <div className="empty-state">Running production checks…</div>;

  return (
    <>
      <div className="toolbar">
        <div className="section-label" style={{ margin: 0 }}>Production Launch Certification</div>
        <div className="spacer" />
        <button className="btn-ghost" onClick={runJourneyTest} disabled={busy} data-testid="run-certification-button"><PlayCircle size={13} /> Run test journey</button>
        <button className="btn-ghost" onClick={runChecks} disabled={busy}><RefreshCw size={13} /> Re-run checks</button>
        <button className="btn-primary" onClick={issue} disabled={busy || !checks.certified} data-testid="issue-certificate-button">
          <Award size={14} /> Issue certificate
        </button>
      </div>

      <div className="card" data-testid="certification-summary" style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
        <div style={{ textAlign: "center" }}>
          <div className="score-ring" style={{ color: checks.certified ? "#1D9E75" : "#1F75A8" }}>{checks.score}%</div>
          <div className="detail-label" style={{ marginTop: 4 }}>Certification score</div>
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <span className={`cert-badge ${checks.certified ? "cert-yes" : "cert-no"}`}>{checks.certified ? "✓ CERTIFIED" : "✕ NOT CERTIFIED"}</span>
          <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: 12, flexWrap: "wrap" }}>
            <span style={{ color: "#1D9E75" }}>✓ {checks.passed} passed</span>
            <span style={{ color: "#BA7517" }}>⚠ {checks.warnings} warnings</span>
            <span style={{ color: "#E24B4A" }}>✕ {checks.failed} failed</span>
            <span style={{ color: "#E24B4A", fontWeight: 600 }}>{checks.critical_failures.length} critical</span>
          </div>
          <div className="decision-meta" style={{ marginTop: 8 }}>
            Environment: {checks.environment} · MEOS {checks.build_version} · EII {checks.eii_version}
          </div>
        </div>
      </div>

      {issueError && <div className="login-error" style={{ marginTop: 12 }}>{issueError}</div>}

      {checks.critical_failures.length > 0 && (
        <>
          <SectionLabel>Critical failures — must resolve before certification</SectionLabel>
          <div className="card" style={{ borderColor: "#E24B4A" }}>
            {checks.critical_failures.map((c, i) => (
              <div className="check-row" key={i}>
                <XCircle size={16} color="#E24B4A" />
                <div><div className="check-name">{c.name} <span className="crit-tag">CRITICAL</span></div>
                  {c.recommended_action && <div className="check-action">{c.recommended_action}</div>}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {certificate && (
        <>
          <SectionLabel>Production Launch Certificate — issued</SectionLabel>
          <div className="certificate" data-testid="issued-certificate">
            <div className="seal">Marrsing Executive Operating System™</div>
            <div className="ct-title">Production Launch Certificate</div>
            <div className="ct-id">{certificate.certificate_id}</div>
            <div className="ct-grid">
              <div><div className="ct-label">Issued</div><div className="ct-value">{new Date(certificate.issued_at).toLocaleString()}</div></div>
              <div><div className="ct-label">Environment</div><div className="ct-value">{certificate.environment}</div></div>
              <div><div className="ct-label">Build</div><div className="ct-value">MEOS {certificate.build_version}</div></div>
              <div><div className="ct-label">Checks Passed</div><div className="ct-value">{certificate.checks_passed} / {certificate.total_checks}</div></div>
              <div><div className="ct-label">Score</div><div className="ct-value">{certificate.score}%</div></div>
              <div><div className="ct-label">Certifying Executive</div><div className="ct-value">{certificate.certifying_executive?.name}</div></div>
            </div>
            <div className="ct-status">✓ {certificate.status} · {certificate.digital_status}</div>
          </div>
          <div className="toolbar" style={{ marginTop: 12 }}>
            <button className="btn-ghost" onClick={() => printCertificate(certificate)} data-testid="export-pdf-button"><Printer size={13} /> Export PDF</button>
            <button className="btn-ghost" onClick={() => download(`${certificate.certificate_id}.json`, JSON.stringify(certificate, null, 2), "application/json")}><FileJson size={13} /> Export JSON</button>
            <button className="btn-ghost" onClick={() => download(`${certificate.certificate_id}.txt`, summaryText(certificate), "text/plain")}><FileText size={13} /> Executive summary</button>
          </div>
        </>
      )}

      <SectionLabel>Certification checklist — 7 categories</SectionLabel>
      <div data-testid="certification-checklist">
        {checks.categories.map((cat) => (
          <div className="card" key={cat.name}>
            <div className="cat-header">
              <div className="cat-title">{cat.name}</div>
              <span className="source-badge" style={{ background: cat.passed === cat.total ? "#EAF3DE" : "#EEF1F4", color: cat.passed === cat.total ? "#3B6D11" : "#55606B" }}>{cat.passed}/{cat.total}</span>
            </div>
            {cat.checks.map((c, i) => (
              <div className="check-row" key={i}>
                <CheckIcon status={c.status} />
                <div style={{ flex: 1 }}>
                  <div className="check-name">{c.name} {c.critical && <span className="crit-tag">CRITICAL</span>}</div>
                  {c.detail && <div className="check-detail">{c.detail}</div>}
                  {c.status !== "pass" && c.recommended_action && <div className="check-action">→ {c.recommended_action}</div>}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      <SectionLabel>Certification history</SectionLabel>
      <div className="card">
        {history.length === 0 && <div className="empty-state">No certificates issued yet.</div>}
        {history.map((c) => (
          <div className="decision-item" key={c.certificate_id} onClick={() => setCertificate(c)}>
            <ShieldCheck size={15} color="#1D9E75" />
            <div style={{ flex: 1 }}>
              <div className="decision-title" style={{ fontWeight: 500 }}>{c.certificate_id}</div>
              <div className="decision-meta">{new Date(c.issued_at).toLocaleString()} · {c.environment} · {c.certifying_executive?.name}</div>
            </div>
            <span className="source-badge src-live">{c.score}% · {c.status}</span>
          </div>
        ))}
      </div>
    </>
  );
}
